import React, { useEffect, useState } from "react";
import { api, errorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BrregNameSearch, BrregOrgField } from "@/components/BrregLookup";
import { entityToFields } from "@/lib/brreg";
import { toast } from "sonner";
import { Loader2, Building2, User } from "lucide-react";

const EMPTY = { type: "company", name: "", org_number: "", address: "", postal_code: "", city: "", country: "Norge", email: "", phone: "", website: "", default_category_id: "ingen", notes: "" };

/**
 * Opprett/rediger leverandør i et sidepanel. Firma kan hentes fra BRREG (navn/org.nr/adresse).
 * onSaved(supplier) kalles ved suksess.
 */
export const SupplierSheet = ({ open, onOpenChange, supplier, categories = [], onSaved }) => {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [brregApplied, setBrregApplied] = useState("");
  const editing = !!supplier?.id;

  useEffect(() => {
    if (open) {
      setForm(supplier ? { ...EMPTY, ...Object.fromEntries(Object.entries(supplier).filter(([, v]) => v !== null && v !== undefined)), type: supplier.type || "company", default_category_id: supplier.default_category_id || "ingen" } : EMPTY);
      setError("");
      setBrregApplied("");
    }
  }, [open, supplier]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const applyBrreg = (entity) => {
    const b = entityToFields(entity);
    setBrregApplied(b.org_number);
    setForm((f) => ({ ...f, type: "company", name: b.name || f.name, org_number: b.org_number || f.org_number, address: b.address || f.address, postal_code: b.postal_code || f.postal_code, city: b.city || f.city, country: b.country || f.country || "Norge", website: b.website || f.website }));
    if (entity?.bankrupt || entity?.deleted_at) toast.warning(entity.deleted_at ? "Virksomheten er slettet i Enhetsregisteret" : "Virksomheten er registrert som konkurs", { description: "Kontroller før du betaler regninger fra denne leverandøren." });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setError("Navn er påkrevd"); return; }
    setSaving(true);
    setError("");
    const payload = {
      name: form.name.trim(), type: form.type,
      org_number: form.type === "company" ? (form.org_number || null) : null,
      address: form.address || null, postal_code: form.postal_code || null, city: form.city || null, country: form.country || null,
      email: form.email || null, phone: form.phone || null, website: form.website || null,
      default_category_id: form.default_category_id === "ingen" ? null : form.default_category_id,
      notes: form.notes || null,
    };
    try {
      const res = editing ? await api.put(`/suppliers/${supplier.id}`, payload) : await api.post("/suppliers", payload);
      toast.success(editing ? "Leverandør oppdatert" : "Leverandør opprettet");
      onSaved?.(res.data);
      onOpenChange(false);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const isCompany = form.type === "company";

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg bg-card overflow-y-auto" data-testid="supplier-sheet">
        <SheetHeader>
          <SheetTitle>{editing ? "Rediger leverandør" : "Ny leverandør"}</SheetTitle>
          <SheetDescription>{editing ? "Navnet oppdateres på alle regninger fra leverandøren." : "Legg inn firma via Brønnøysundregistrene, eller en privatperson. Regninger kobles automatisk på org.nr eller navn."}</SheetDescription>
        </SheetHeader>
        <form onSubmit={submit} className="mt-6 space-y-5" noValidate>
          <ToggleGroup type="single" value={form.type} onValueChange={(v) => v && setForm((f) => ({ ...f, type: v }))} className="grid grid-cols-2 gap-1 rounded-lg border bg-secondary p-1" data-testid="supplier-type-toggle">
            <ToggleGroupItem value="company" className="gap-2 rounded-md data-[state=on]:bg-card data-[state=on]:shadow-sm" data-testid="supplier-type-company"><Building2 className="h-4 w-4" /> Firma</ToggleGroupItem>
            <ToggleGroupItem value="person" className="gap-2 rounded-md data-[state=on]:bg-card data-[state=on]:shadow-sm" data-testid="supplier-type-person"><User className="h-4 w-4" /> Privatperson</ToggleGroupItem>
          </ToggleGroup>

          {isCompany ? (
            <div className="space-y-1.5">
              <Label htmlFor="s-org">Org.nr</Label>
              <BrregOrgField id="s-org" value={form.org_number || ""} onChange={(v) => setForm((f) => ({ ...f, org_number: v }))} onApply={applyBrreg} autoApply={!form.name.trim()} appliedOrgNumber={brregApplied} testIdPrefix="supplier" />
            </div>
          ) : null}

          <div className="space-y-1.5">
            <Label htmlFor="s-name">{isCompany ? "Firmanavn" : "Navn"} *</Label>
            {isCompany ? (
              <BrregNameSearch id="s-name" value={form.name} onChange={(v) => setForm((f) => ({ ...f, name: v }))} onSelect={applyBrreg} placeholder="Søk etter firma i Enhetsregisteret, eller skriv navnet" testIdPrefix="supplier" required />
            ) : (
              <Input id="s-name" value={form.name} onChange={set("name")} required data-testid="supplier-name-input" />
            )}
          </div>

          <div className="space-y-1.5"><Label htmlFor="s-address">Adresse</Label><Input id="s-address" value={form.address || ""} onChange={set("address")} data-testid="supplier-address-input" /></div>
          <div className="grid grid-cols-[110px_1fr] gap-4">
            <div className="space-y-1.5"><Label htmlFor="s-zip">Postnr</Label><Input id="s-zip" inputMode="numeric" value={form.postal_code || ""} onChange={set("postal_code")} data-testid="supplier-zip-input" /></div>
            <div className="space-y-1.5"><Label htmlFor="s-city">Sted</Label><Input id="s-city" value={form.city || ""} onChange={set("city")} data-testid="supplier-city-input" /></div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label htmlFor="s-email">E-post</Label><Input id="s-email" type="email" value={form.email || ""} onChange={set("email")} data-testid="supplier-email-input" /></div>
            <div className="space-y-1.5"><Label htmlFor="s-phone">Telefon</Label><Input id="s-phone" value={form.phone || ""} onChange={set("phone")} data-testid="supplier-phone-input" /></div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label htmlFor="s-country">Land</Label><Input id="s-country" value={form.country || ""} onChange={set("country")} data-testid="supplier-country-input" /></div>
            <div className="space-y-1.5">
              <Label htmlFor="s-category">Standardkategori</Label>
              <Select value={form.default_category_id || "ingen"} onValueChange={(v) => setForm((f) => ({ ...f, default_category_id: v }))}>
                <SelectTrigger id="s-category" data-testid="supplier-category-input"><SelectValue placeholder="Ingen" /></SelectTrigger>
                <SelectContent className="bg-card"><SelectItem value="ingen">Ingen standardkategori</SelectItem>{categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5"><Label htmlFor="s-notes">Notater</Label><Textarea id="s-notes" rows={2} value={form.notes || ""} onChange={set("notes")} placeholder="f.eks. kundenummer 448812" data-testid="supplier-notes-input" /></div>
          {error ? <p className="text-sm text-destructive" role="alert" data-testid="supplier-form-error">{error}</p> : null}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="supplier-cancel-button">Avbryt</Button>
            <Button type="submit" disabled={saving} data-testid="supplier-save-button">{saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}{editing ? "Lagre" : "Opprett leverandør"}</Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
};
